import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TaskProvider } from './contexts/TaskContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { RequireAuth } from './components/Auth/RequireAuth';
import { Auth } from './pages/Auth';
import { ProfileProvider } from './contexts/ProfileContext';
import { ResetPassword } from './pages/ResetPassword';
import App from './App';
import { TaskManager } from './pages/TaskManager';
import { BrainDump } from './pages/BrainDump';
import { BrainDumpView } from './pages/BrainDumpView';
import { Lists } from './pages/Lists';
import { InnerCircle } from './pages/InnerCircle';
import { Business } from './pages/Business';
import { TransactionView } from './pages/TransactionView';
import { Calendar } from './pages/Calendar';
import { ChallengesNews } from './pages/ChallengesNews';
import { ChallengeEdit } from './pages/ChallengeEdit';
import { Profile } from './pages/Profile';
import { MoodBoard } from './pages/MoodBoard';
import { Subscription } from './pages/Subscription';
import { OAuthCallback } from './pages/OAuthCallback';
import { Travel } from './pages/Travel';
import { ErrorBoundary } from './components/ErrorBoundary';
import './index.css';

// Create router with auth-protected routes
const router = createBrowserRouter([
  { 
    path: '/auth', 
    element: <Auth /> 
  },
  { 
    path: '/reset-password', 
    element: <ResetPassword /> 
  },
  { 
    path: '/oauth/callback', 
    element: <OAuthCallback /> 
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <App />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/tasks',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <TaskManager />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/brain-dump',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <BrainDump />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/brain-dump/:id',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <BrainDumpView />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/lists',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Lists />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/mood-board',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <MoodBoard />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/inner-circle',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <InnerCircle />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/business',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Business />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/business/transactions/:id',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <TransactionView />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/calendar',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Calendar />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/challenges',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <ChallengesNews />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/challenges/edit/:id',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <ChallengeEdit />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/profile',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Profile />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/subscription',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Subscription />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
  {
    path: '/travel',
    element: (
      <RequireAuth>
        <ProfileProvider>
          <SubscriptionProvider>
            <TaskProvider>
              <Travel />
            </TaskProvider>
          </SubscriptionProvider>
        </ProfileProvider>
      </RequireAuth>
    ),
  },
]);

// Get root element
const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element not found');

// Create root only once
const root = createRoot(rootElement);

// Render app with proper provider hierarchy
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);